import { useEffect } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, Outlet, useLocation, useNavigate, useRevalidator } from 'react-router';
import { signOut, meQuery } from '../features/authentication/api/auth';
import { queryClient } from '../lib/query-client';

type WorkspaceLink = { label: string; to: string; external?: boolean };
type NavigationGroup = { label: string; links: WorkspaceLink[] };

export const workspaceNavigationGroups: NavigationGroup[] = [
  {
    label: 'Access',
    links: [
      { label: 'Workspace', to: '/' },
      { label: 'Users', to: '/users' },
      { label: 'Two-factor authentication', to: '/security/two-factor' },
    ],
  },
  {
    label: 'Editorial',
    links: [
      { label: 'Articles', to: '/articles' },
      { label: 'Media', to: '/media' },
      { label: 'Tags', to: '/tags' },
      { label: 'Series', to: '/series' },
      { label: 'Questions', to: '/questions' },
      { label: 'Quiz sets', to: '/quiz-sets' },
    ],
  },
  {
    label: 'Insights & operations',
    links: [
      { label: 'Overview', to: '/insights' },
      { label: 'Question signals', to: '/insights/questions' },
      { label: 'Topic signals', to: '/insights/tags' },
      { label: 'Search signals', to: '/insights/searches' },
      { label: 'Opportunities', to: '/insights/opportunities' },
      { label: 'Search index', to: '/index' },
      { label: 'Settings', to: '/settings' },
      { label: 'Audit trail', to: '/audit' },
    ],
  },
  {
    label: 'Public',
    links: [
      { label: 'Journal', to: '/journal', external: true },
      { label: 'Quizzes', to: '/quiz', external: true },
      { label: 'Search', to: '/search', external: true },
    ],
  },
];

export function RouterLayout({ workspace = false }: { workspace?: boolean }) {
  const location = useLocation();
  const navigate = useNavigate();
  const isAuthenticationRoute = [
    '/login',
    '/account',
    '/verify-email',
    '/reset-password',
    '/change-password',
  ].includes(location.pathname);
  const isWorkspaceRoute = workspace && !isAuthenticationRoute;
  const profile = useQuery({ ...meQuery, enabled: isWorkspaceRoute });
  const logout = useMutation({
    mutationFn: signOut,
    onSuccess: async () => {
      queryClient.clear();
      await navigate('/login');
    },
  });
  useEffect(() => {
    const heading = document.querySelector<HTMLElement>('main h1');
    if (heading) {
      heading.tabIndex = -1;
      heading.focus();
    }
  }, [location.pathname]);

  return (
    <>
      <a className="skip-link" href="#application-content">
        Skip to content
      </a>
      {isWorkspaceRoute ? (
        <div className="internal-shell">
          <aside className="internal-rail" aria-label="Workspace navigation">
            <Link className="brand" to="/">
              <img className="brand-mark" src="/myblog-mark.svg" alt="" />
              <span>MyBlog</span>
            </Link>
            <p className="rail-note">Editorial workspace</p>
            <nav className="internal-groups" aria-label="Workspace sections">
              {workspaceNavigationGroups.map((group) => (
                <section className="internal-nav-group" key={group.label}>
                  <h2>{group.label}</h2>
                  {group.links.map((link) =>
                    link.external ? (
                      <a key={link.to} href={link.to}>
                        {link.label}
                      </a>
                    ) : (
                      <Link
                        key={link.to}
                        to={link.to}
                        aria-current={
                          location.pathname === link.to ||
                          (link.to !== '/' && location.pathname.startsWith(`${link.to}/`))
                            ? 'page'
                            : undefined
                        }
                      >
                        {link.label}
                      </Link>
                    ),
                  )}
                </section>
              ))}
            </nav>
            <p className="rail-foot">Internal management</p>
          </aside>
          <div className="internal-content" id="application-content" tabIndex={-1}>
            <header className="internal-account-bar" aria-label="Account">
              <span>{profile.data?.displayName}</span>
              <button
                className="button secondary"
                type="button"
                onClick={() => logout.mutate()}
                disabled={logout.isPending}
              >
                {logout.isPending ? 'Signing out…' : 'Sign out'}
              </button>
            </header>
            {logout.isError && (
              <p className="notice error internal-account-error" role="alert">
                {logout.error.message}
              </p>
            )}
            <Outlet />
          </div>
        </div>
      ) : (
        <div id="application-content" tabIndex={-1}>
          <Outlet />
        </div>
      )}
    </>
  );
}

export function WorkspaceRouterLayout() {
  return <RouterLayout workspace />;
}

export function RouterErrorBoundary() {
  const revalidator = useRevalidator();

  return (
    <main className="management-page" id="application-content" tabIndex={-1}>
      <section className="management-content" aria-labelledby="route-error-title">
        <h1 id="route-error-title">This page is unavailable.</h1>
        <p className="notice error" role="alert">
          The page could not be loaded. Check your connection and try again.
        </p>
        <button
          className="button"
          type="button"
          onClick={() => revalidator.revalidate()}
          disabled={revalidator.state === 'loading'}
        >
          {revalidator.state === 'loading' ? 'Retrying…' : 'Try again'}
        </button>
        <p>
          <Link to="/">Return to the workspace</Link>
        </p>
      </section>
    </main>
  );
}
